import { WorkspaceFramework } from './Workspace';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from './WorkspaceView';

export enum Column {
  none = 'none',
  taskId = 'taskId',
  taskType = 'taskType',
  subitems = 'subitems',
  sprintStatus = 'sprintStatus',
  epic = 'epic',
  assignee = 'assignee',
  status = 'status',
  deadline = 'deadline',
  startDate = 'startDate',
  priority = 'priority',
  estimate = 'estimate',
  label = 'label',
  blocking = 'blocking',
  blockedBy = 'blockedBy',
  createdAt = 'createdAt',
  updatedAt = 'updatedAt',
  space = 'space',
}

export type WorkspaceViewColumn = {
  hidden: Column[];
  order: Column[];
};

export type ViewColumn = {
  id: Column;
  selected: boolean;
  orderIndex: number;
};

export const ViewColumnUtils = {
  getTitle(column: Column, framework: WorkspaceFramework): string {
    switch (column) {
      case Column.none:
        return 'None';
      case Column.sprintStatus:
        return 'Stage';
      case Column.assignee:
        return 'Owner';
      case Column.epic:
        return 'Epic';
      case Column.status:
        return 'Status';
      case Column.deadline:
        return 'Target Date';
      case Column.startDate:
        return 'Start Date';
      case Column.priority:
        return 'Priority';
      case Column.estimate:
        return 'Estimate';
      case Column.taskId:
        return 'ID';
      case Column.taskType:
        return 'Type';
      case Column.subitems:
        return 'Subitems';
      case Column.label:
        return 'Label';
      case Column.blocking:
        return 'Blocking';
      case Column.blockedBy:
        return 'Blocked By';
      case Column.createdAt:
        return 'Created';
      case Column.updatedAt:
        return 'Updated';
      case Column.space:
        return 'Space';
    }
  },
  sortByColumns(framework: WorkspaceFramework, view: Partial<WorkspaceView>): ViewColumn[] {
    // Filter out subitems column for sortByColumns
    const defaultColumns = ViewColumnUtils.defaultColumns(framework, view).filter(
      (column) => column.id !== Column.subitems && column.id !== Column.label
    );
    if (view.entityType === WorkspaceViewEntityType.epic) {
      return [
        { id: Column.taskId, selected: true, orderIndex: 0 },
        ...defaultColumns,
        { id: Column.createdAt, selected: true, orderIndex: defaultColumns.length + 1 },
        { id: Column.updatedAt, selected: true, orderIndex: defaultColumns.length + 2 },
      ];
    }
    return [
      { id: Column.taskId, selected: true, orderIndex: 0 },
      { id: Column.taskType, selected: true, orderIndex: 1 },
      ...defaultColumns,
      { id: Column.createdAt, selected: true, orderIndex: defaultColumns.length + 2 },
      { id: Column.updatedAt, selected: true, orderIndex: defaultColumns.length + 3 },
    ];
  },
  groupByColumns(framework: WorkspaceFramework, view: Partial<WorkspaceView>): ViewColumn[] {
    const noneOption: ViewColumn = { id: Column.none, selected: true, orderIndex: -1 };
    if (view.entityType === WorkspaceViewEntityType.epic) {
      return [
        noneOption,
        { id: Column.assignee, selected: true, orderIndex: 0 },
        { id: Column.startDate, selected: true, orderIndex: 1 },
        { id: Column.deadline, selected: true, orderIndex: 2 },
      ];
    }
    return [
      noneOption,
      { id: Column.taskType, selected: true, orderIndex: 0 },
      ...ViewColumnUtils.defaultColumns(framework, view).filter((column) => column.id !== Column.subitems),
    ];
  },
  defaultColumns(framework: WorkspaceFramework, view: Partial<WorkspaceView>): ViewColumn[] {
    const { group, entityType } = view;
    if (entityType === WorkspaceViewEntityType.epic) {
      return [
        { id: Column.assignee, selected: true, orderIndex: 0 },
        { id: Column.startDate, selected: true, orderIndex: 1 },
        { id: Column.deadline, selected: true, orderIndex: 2 },
      ];
    }
    const isStageContextual = group === WorkspaceViewGroup.triage || group === WorkspaceViewGroup.archive;
    const sprintKanbanStatusArray =
      (framework === WorkspaceFramework.none && !isStageContextual) ||
      group === WorkspaceViewGroup.sprint ||
      group === WorkspaceViewGroup.kanban
        ? []
        : [{ id: Column.sprintStatus, selected: true, orderIndex: 2 }];
    const subitemsArray =
      group === WorkspaceViewGroup.triage ? [] : [{ id: Column.subitems, selected: true, orderIndex: 0 }];
    return [
      ...subitemsArray,
      { id: Column.epic, selected: true, orderIndex: 1 },
      ...sprintKanbanStatusArray,
      { id: Column.label, selected: true, orderIndex: 3 },
      { id: Column.assignee, selected: true, orderIndex: 4 },
      { id: Column.status, selected: true, orderIndex: 5 },
      { id: Column.priority, selected: true, orderIndex: 6 },
      { id: Column.estimate, selected: true, orderIndex: 7 },
      { id: Column.startDate, selected: true, orderIndex: 8 },
      { id: Column.deadline, selected: true, orderIndex: 9 },
      { id: Column.blocking, selected: true, orderIndex: 10 },
      { id: Column.blockedBy, selected: true, orderIndex: 11 },
    ];
  },
  updateAndSortColumn(framework: WorkspaceFramework, view: WorkspaceView): ViewColumn[] {
    const list: ViewColumn[] = ViewColumnUtils.defaultColumns(framework, view);
    const config = view.columns;
    if (!config) {
      return list;
    }
    const { hidden, order } = config;

    if (!hidden || !order) {
      return list;
    }

    const updatedList = list.map((item) => ({
      ...item,
      selected: !hidden.includes(item.id),
      orderIndex: order.indexOf(item.id) !== -1 ? order.indexOf(item.id) : item.orderIndex,
    }));

    updatedList.sort((a, b) => a.orderIndex - b.orderIndex);

    return updatedList;
  },
  updateColumn(framework: WorkspaceFramework, view: WorkspaceView): ViewColumn[] {
    const list: ViewColumn[] = ViewColumnUtils.defaultColumns(framework, view);
    const config = view.columns;
    if (!config) {
      return list;
    }
    const { hidden, order } = config;

    if (!hidden || !order) {
      return list;
    }

    const updatedList = list.map((item) => ({
      ...item,
      selected: !hidden.includes(item.id),
      orderIndex: item.orderIndex,
    }));

    updatedList.sort((a, b) => a.orderIndex - b.orderIndex);

    return updatedList;
  },
};
